import { Link } from 'react-router-dom'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Users } from 'lucide-react'

// §0.2 jargon scrub: "calibration ledger" → "learning record"; "memory stream" → "workspace memory"
// §0.3 compliance: final-review phrase in methodology callout

const CARDS = [
  {
    title: 'Evidence infrastructure',
    desc: 'Centralize surveys, interviews, open-ends, and transcripts into a structured workspace memory that the whole team can query. Past work feeds future pre-reads automatically.',
  },
  {
    title: 'Stakeholder pre-reads',
    desc: 'When a product manager asks "what do customers think about X?" — answer in hours, not weeks, with an evidence-backed pre-read and clear indication of which segments need more evidence.',
  },
  {
    title: '1:1 modeling when it matters',
    desc: 'For studies demanding per-customer prediction — preference-center, opt-out modeling, channel strategy — build a twin panel with backtest-validated accuracy and run bulk queries before committing field budget.',
  },
  {
    title: 'Learning record',
    desc: 'Track how well pre-reads match real validation results over time. The learning record shows which evidence types and question patterns have the strongest track record in your workspace.',
  },
]

const BUYERS = [
  {
    role: 'Head of Consumer Insights',
    desc: 'Show ROI on past research by reusing it for live decisions. Build the case for evidence infrastructure, not just syndicated research.',
  },
  {
    role: 'Research ops lead',
    desc: 'One shared evidence layer for the whole team. New researchers onboard using the accumulated study library instead of starting from scratch.',
  },
  {
    role: 'Senior researcher',
    desc: 'Answer stakeholder questions in an afternoon using past studies. Spend field budget on the things that aren\'t already answered.',
  },
]

export function ResearchTeamsPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        {/* Hero */}
        <div className="space-y-5 max-w-3xl">
          <p className="text-sm font-medium text-human-teal uppercase tracking-wide">For in-house research teams</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Stop starting every study from zero.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Your team generates insights constantly. Echosome builds a shared evidence layer from that work so you can answer new questions from existing data — and know exactly when you need to run new research.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/app/demo">
              <Button variant="primary" size="lg">Try the demo</Button>
            </Link>
            <Link to="/product">
              <Button variant="outline" size="lg">See how it works</Button>
            </Link>
          </div>
        </div>

        {/* Core value props */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CARDS.map(item => (
            <Card key={item.title}>
              <h3 className="font-medium text-deep-ink mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600">{item.desc}</p>
            </Card>
          ))}
        </div>

        {/* Compounding research */}
        <div className="bg-soft-panel rounded-xl border border-border-gray p-6 space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Why compounding research matters</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Most research teams re-answer the same questions across projects because evidence lives in decks, CSVs, and individual researchers' heads. Echosome keeps evidence structured and queryable so each new study builds on the last. Over time, more pre-reads are answerable from existing evidence — which means field budget goes further and turnaround times shrink.
          </p>
          <p className="text-sm text-slate-500 italic">
            Research pre-reads improve as evidence accumulates. Initial results use architectural pre-reads; calibration accuracy improves once real validation outcomes have been recorded.
          </p>
          <Link to="/research/methodology/ablation" className="text-sm text-echo-indigo font-medium hover:underline">
            See our methodology →
          </Link>
        </div>

        {/* Who buys this */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Who uses Echosome on in-house teams</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {BUYERS.map(b => (
              <Card key={b.role}>
                <h3 className="font-medium text-deep-ink mb-2 text-sm">{b.role}</h3>
                <p className="text-sm text-slate-600">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Twin Panel use cases */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-echo-indigo" />
            <h2 className="text-2xl font-medium text-deep-ink">When per-customer modeling matters</h2>
          </div>
          <p className="text-slate-600 text-sm max-w-2xl">
            Echo Panel gives you directional segment-level pre-reads fast. For engagements where individual-level accuracy is the deliverable, Twin Panel goes further.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                title: 'Preference-center and opt-out modeling',
                desc: 'Upload prior-wave data. Build twins. Ask "which category framing reduces opt-out intent most?" across the full panel — predicted distribution + per-segment cross-tab in ~40 seconds.',
              },
              {
                title: 'Channel-strategy and onboarding comprehension',
                desc: 'Per-respondent predicted preference lets you see how different activation messages land across microsegments — before any copy goes to legal.',
              },
              {
                title: 'Message and concept testing',
                desc: 'Run 10 message variants against 1,200 twins in one bulk query. Surface the segments with dissent before you brief the creative team.',
              },
              {
                title: 'Advisory board preparation',
                desc: 'Know going in which segments are outliers, which questions have low twin confidence, and which responses warrant live probing.',
              },
            ].map(u => (
              <Card key={u.title}>
                <h3 className="font-medium text-deep-ink mb-2 text-sm">{u.title}</h3>
                <p className="text-sm text-slate-600">{u.desc}</p>
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Twin Panel is available on Researcher and above plans. Accuracy is reported per twin with Wilson 95% CI — excluded twins are not included in bulk query results.
          </p>
        </div>

        {/* Trust strip */}
        <div className="border border-border-gray rounded-xl p-5 space-y-2">
          <p className="text-sm text-slate-700 font-medium">Echosome is decision support for research.</p>
          <ul className="space-y-1">
            {[
              'Pre-reads cite the evidence that supports them.',
              'Weak evidence and disagreement are shown explicitly, not hidden.',
              'Final review stays with your legal and compliance teams.',
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <span className="text-echo-indigo mt-0.5 shrink-0">→</span>
                {b}
              </li>
            ))}
          </ul>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
